import { useCallback, useEffect, useRef } from 'react';

/** 화면 아래에서 가려진 높이(모바일 키보드 등) — px */
export const VISUAL_VIEWPORT_BOTTOM_VAR = '--visual-viewport-bottom';
/** visualViewport 가 위로 밀린 정도(offsetTop, 0 이상) — px */
export const VISUAL_VIEWPORT_TOP_VAR = '--visual-viewport-top';

const readVisualViewportInset = () => {
  const visualViewport = window.visualViewport;
  if (!visualViewport || visualViewport.height <= 0)
    return { bottom: 0, top: 0 };
  const top = Math.max(visualViewport.offsetTop, 0);
  return {
    bottom: Math.max(window.innerHeight - top - visualViewport.height, 0),
    top,
  };
};

/**
 * 모바일 키보드 등으로 visualViewport 가 줄면 붙인 요소에 측정값을 싣는다 — 위치는 요소의 className 이 정한다.
 * - `--visual-viewport-bottom` · `--visual-viewport-top` (px)
 * - `data-keyboard="open"` — 화면 아래가 가려졌을 때만
 * 키보드 애니메이션 중 리렌더하지 않도록 style·dataset 을 직접 고친다. 꺼지면 모두 지운다.
 */
export const useVisualViewportInset = <T extends HTMLElement>(
  enabled: boolean,
) => {
  const nodeRef = useRef<T | null>(null);

  const update = useCallback(() => {
    const node = nodeRef.current;
    if (!node) return;
    if (!enabled) {
      node.style.removeProperty(VISUAL_VIEWPORT_BOTTOM_VAR);
      node.style.removeProperty(VISUAL_VIEWPORT_TOP_VAR);
      delete node.dataset.keyboard;
      return;
    }
    const { bottom, top } = readVisualViewportInset();
    node.style.setProperty(VISUAL_VIEWPORT_BOTTOM_VAR, `${bottom}px`);
    node.style.setProperty(VISUAL_VIEWPORT_TOP_VAR, `${top}px`);
    if (bottom > 0) node.dataset.keyboard = 'open';
    else delete node.dataset.keyboard;
  }, [enabled]);

  const setNode = useCallback(
    (node: T | null) => {
      nodeRef.current = node;
      update();
    },
    [update],
  );

  useEffect(() => {
    update();
    if (!enabled) return;
    const visualViewport = window.visualViewport;
    window.addEventListener('resize', update);
    visualViewport?.addEventListener('resize', update);
    visualViewport?.addEventListener('scroll', update);
    return () => {
      window.removeEventListener('resize', update);
      visualViewport?.removeEventListener('resize', update);
      visualViewport?.removeEventListener('scroll', update);
    };
  }, [enabled, update]);

  return { nodeRef, setNode };
};
